import { inject, Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  AssetVerificationEvent,
  AssetVerificationEventType,
  AssetVerificationLaunchOptions,
  AssetVerificationProvider,
} from './asset-verification-provider.interface';
import { LoggingService } from '@ld.consumer-portal/utils';

export type FinicityConnectEventType = AssetVerificationEventType;

export type FinicityConnectEvent = AssetVerificationEvent;

export type FinicityConnectOptions = AssetVerificationLaunchOptions;

declare global {
  interface Window {
    finicityConnect?: {
      launch: (url: string, options: FinicityConnectCallbacks) => void;
      destroy: () => void;
    };
  }
}

interface FinicityConnectCallbacks {
  selector?: string;
  overlay?: string;
  success?: (event: unknown) => void;
  cancel?: (event: unknown) => void;
  error?: (error: unknown) => void;
  loaded?: () => void;
  route?: (event: unknown) => void;
  user?: (event: unknown) => void;
}

@Injectable({
  providedIn: 'root',
})
export class FinicityConnectService implements AssetVerificationProvider {
  private readonly ngZone = inject(NgZone);
  private readonly loggingService = inject(LoggingService);
  private readonly events$ = new Subject<AssetVerificationEvent>();
  private isActive = false;

  isLoaded(): boolean {
    return typeof window.finicityConnect !== 'undefined';
  }

  launch(url: string, options: FinicityConnectOptions = {}): Observable<FinicityConnectEvent> {
    if (!this.isLoaded()) {
      this.loggingService.logWarning('Finicity Connect SDK is not loaded');
      this.events$.next({ type: 'error', data: 'Finicity Connect SDK is not loaded' });
      return this.events$.asObservable();
    }

    if (this.isActive) {
      this.destroy();
    }

    this.isActive = true;

    window.finicityConnect?.launch(url, {
      selector: options.selector || '#finicity-container',
      overlay: options.overlay || 'rgba(0, 0, 0, 0.5)',
      success: (event: unknown) => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'success', data: event });
          this.isActive = false;
        });
      },
      cancel: (event: unknown) => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'cancel', data: event });
          this.isActive = false;
        });
      },
      error: (error: unknown) => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'error', data: error });
          this.isActive = false;
        });
      },
      loaded: () => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'loaded' });
        });
      },
      route: (event: unknown) => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'route', data: event });
        });
      },
      user: (event: unknown) => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'user', data: event });
        });
      },
    });

    return this.events$.asObservable();
  }

  destroy(): void {
    if (this.isLoaded() && this.isActive) {
      window.finicityConnect?.destroy();
      this.isActive = false;
    }
  }

  getEvents(): Observable<FinicityConnectEvent> {
    return this.events$.asObservable();
  }

}
